import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/helpers";

/**
 * Shared kind picker for the Signals forms (a bespoke listbox combobox).
 *
 * New Signal and Edit Signal share one implementation: open/close state,
 * aria-activedescendant tracking, roving keyboard nav (ArrowUp/Down/Home/End/
 * Enter/Space/Escape per ARIA APG), and an outside-click close.
 *
 * The caller owns the options list (CreateSignalForm passes SIGNAL_KIND_OPTIONS;
 * EditPendingSignal puts the signal's current kind first when it is outside that
 * set), the value, and the onChange. `id` namespaces the listbox + option ids so
 * two instances never collide.
 */
export default function KindSelect({
  value,
  onChange,
  options,
  id = "kind",
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  id?: string;
  label?: string;
}) {
  const [selectOpen, setSelectOpen] = useState(false);
  // The keyboard-active option while the listbox is open (ARIA APG), surfaced to
  // AT via aria-activedescendant on the field and to sighted users as the hover fill.
  const [activeIndex, setActiveIndex] = useState(0);
  const selectRef = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? value.replace(/_/g, " ");
  const LISTBOX_ID = `${id}-listbox`;
  const optionId = (i: number) => `${id}-opt-${i}`;

  const openMenu = () => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setSelectOpen(true);
  };
  const chooseActive = () => {
    const opt = options[activeIndex];
    if (opt) onChange(opt.value);
    setSelectOpen(false);
  };

  // Close when clicking outside the picker.
  useEffect(() => {
    if (!selectOpen) return;
    const onDown = (e: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [selectOpen]);

  const onFieldKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (selectOpen) chooseActive();
        else openMenu();
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!selectOpen) openMenu();
        else setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!selectOpen) openMenu();
        else setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case "Home":
        if (selectOpen) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (selectOpen) {
          e.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      case "Escape":
        // Closing the list is this key's whole job while it is open; the dialog
        // around it only closes on an Escape the list did not use.
        if (selectOpen) {
          e.preventDefault();
          setSelectOpen(false);
        }
        break;
    }
  };

  return (
    <div className="sig-field">
      {label && (
        <span className="sig-field__label sig-field__label--mono" id={`${id}-label`}>
          {label}
        </span>
      )}
      <div className={cn("sig-select", selectOpen && "is-open")} ref={selectRef}>
        <div
          className="sig-input sig-select__field"
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={selectOpen}
          aria-controls={LISTBOX_ID}
          aria-labelledby={label ? `${id}-label ${id}-value` : undefined}
          aria-activedescendant={selectOpen ? optionId(activeIndex) : undefined}
          onClick={() => (selectOpen ? setSelectOpen(false) : openMenu())}
          onKeyDown={onFieldKeyDown}
        >
          <span className="sig-select__value" id={`${id}-value`}>
            {selectedLabel}
          </span>
          <ChevronDown className="sig-select__caret" aria-hidden="true" />
        </div>
        <div className="sig-select__menu" role="listbox" id={LISTBOX_ID}>
          {options.map((o, i) => (
            <div
              key={o.value}
              id={optionId(i)}
              className={cn(
                "sig-select__opt",
                o.value === value && "is-selected",
                selectOpen && activeIndex === i && "is-active",
              )}
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setSelectOpen(false);
              }}
            >
              {o.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
